import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { getAllPatientActivities, getPatientActivityDetails } from "@/lib/api";
import { 
  Activity, Users, Clock, MessageSquare, FileText, Heart, Pill,
  AlertTriangle, Search, RefreshCw, Loader2, ChevronRight, ArrowUpDown,
  ArrowUp, ArrowDown, Filter, Calendar, TrendingUp, Eye
} from "lucide-react";

const ACTIVITY_TYPES = [
  { value: "all", label: "All Activities", icon: Activity },
  { value: "checkin", label: "Check-ins", icon: Heart },
  { value: "journal", label: "Journal Entries", icon: FileText },
  { value: "message", label: "Messages", icon: MessageSquare },
  { value: "medication", label: "Medications", icon: Pill },
];

const SEVERITY_COLORS = {
  alert: "bg-red-100 text-red-700 border-red-200",
  warning: "bg-amber-100 text-amber-700 border-amber-200",
  info: "bg-blue-100 text-blue-700 border-blue-200",
};

export default function AdminActivityDashboard() {
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  
  const [loading, setLoading] = useState(true);
  const [activities, setActivities] = useState([]);
  const [activityCounts, setActivityCounts] = useState({});
  const [sortBy, setSortBy] = useState("created_at");
  const [sortOrder, setSortOrder] = useState("desc");
  const [filterType, setFilterType] = useState("all");
  const [searchTerm, setSearchTerm] = useState("");
  
  // Date range filter
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  
  // Activity list dialog
  const [activityDialogOpen, setActivityDialogOpen] = useState(false);
  const [activityDialogType, setActivityDialogType] = useState('all');
  
  // Patient details modal
  const [selectedPatient, setSelectedPatient] = useState(null);
  const [patientDetails, setPatientDetails] = useState(null);
  const [detailsLoading, setDetailsLoading] = useState(false);
  
  const isAdmin = user?.role && ["admin", "administrator"].includes(user.role);

  useEffect(() => {
    if (!authLoading) {
      if (!isAdmin) {
        toast.error("Admin access required");
        navigate("/");
        return;
      }
      loadActivities();
    }
  }, [user, authLoading, navigate, isAdmin]);

  const loadActivities = async () => {
    try {
      setLoading(true);
      const data = await getAllPatientActivities(sortBy, sortOrder, 200, filterType === "all" ? null : filterType);
      setActivities(data.activities || []);
      setActivityCounts(data.activity_counts || {});
    } catch (error) {
      console.error("Error loading activities:", error);
      toast.error("Failed to load activities");
    } finally {
      setLoading(false);
    }
  };

  const loadPatientDetails = async (patientId, patientName) => {
    setSelectedPatient({ id: patientId, name: patientName });
    setDetailsLoading(true);
    try {
      const data = await getPatientActivityDetails(patientId, 30);
      setPatientDetails(data);
    } catch (error) {
      console.error("Error loading patient details:", error);
      toast.error("Failed to load patient details");
    } finally {
      setDetailsLoading(false);
    }
  };

  const filteredActivities = activities.filter(activity => {
    // Search filter
    if (searchTerm) {
      const search = searchTerm.toLowerCase();
      if (!(
        activity.user_name?.toLowerCase().includes(search) ||
        activity.title?.toLowerCase().includes(search) ||
        activity.description?.toLowerCase().includes(search)
      )) return false;
    }
    
    // Date range filter
    if (dateFrom) {
      const activityDate = new Date(activity.created_at);
      if (activityDate < new Date(dateFrom)) return false;
    }
    if (dateTo) {
      const activityDate = new Date(activity.created_at);
      if (activityDate > new Date(dateTo + 'T23:59:59')) return false;
    }
    
    return true;
  });
  
  const handleStatClick = (type) => {
    setActivityDialogType(type);
    setActivityDialogOpen(true);
  };
  
  const getActivitiesByType = (type) => {
    return filteredActivities.filter(a => 
      type === 'all' || a.activity_type === type
    );
  };

  const formatTime = (dateString) => {
    if (!dateString) return "";
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / (1000 * 60));
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
    
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  const getActivityIcon = (type) => {
    switch (type) {
      case "checkin": return <Heart className="w-4 h-4" />;
      case "journal": return <FileText className="w-4 h-4" />;
      case "message": return <MessageSquare className="w-4 h-4" />;
      case "medication": return <Pill className="w-4 h-4" />;
      default: return <Activity className="w-4 h-4" />;
    }
  };

  if (authLoading || loading) {
    return (
      <div className="container-max px-6 py-12 flex items-center justify-center min-h-[60vh]">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  if (!isAdmin) {
    return (
      <div className="container-max px-6 py-12 text-center">
        <Activity className="w-16 h-16 mx-auto mb-4 text-primary/30" />
        <h1 className="text-2xl font-serif mb-2">Admin Activity Dashboard</h1>
        <p className="text-muted-foreground mb-6">Admin access required to view patient activities</p>
        <Button onClick={() => navigate("/")}>Go Home</Button>
      </div>
    );
  }

  return (
    <div className="container-max px-6 py-8" data-testid="admin-activity-dashboard">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-serif font-medium flex items-center gap-2">
            <Activity className="w-6 h-6 text-primary" />
            Patient Activity Dashboard
          </h1>
          <p className="text-muted-foreground">Monitor all patient activities across the system</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={loadActivities} disabled={loading}>
            <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-6">
        {ACTIVITY_TYPES.map((type) => {
          const Icon = type.icon;
          const count = type.value === "all" 
            ? Object.values(activityCounts).reduce((a, b) => a + b, 0)
            : activityCounts[type.value] || 0;
          return (
            <Card 
              key={type.value}
              className={`cursor-pointer transition-all hover:shadow-md ${filterType === type.value ? 'ring-2 ring-primary' : ''}`}
              onClick={() => handleStatClick(type.value)}
              data-testid={`stat-${type.value}`}
            >
              <CardContent className="pt-4 pb-4">
                <div className="flex items-center justify-between">
                  <Icon className={`w-5 h-5 ${filterType === type.value ? 'text-primary' : 'text-muted-foreground'}`} />
                  <span className="text-2xl font-bold">{count}</span>
                </div>
                <p className="text-xs text-muted-foreground mt-1">{type.label}</p>
                <p className="text-xs text-primary mt-1">View all →</p>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Date Range Filter */}
      <div className="flex flex-wrap items-center gap-4 mb-6 p-4 bg-slate-50 rounded-lg">
        <div className="flex items-center gap-2">
          <Calendar className="w-4 h-4 text-muted-foreground" />
          <span className="text-sm font-medium">Date Range:</span>
        </div>
        <div className="flex items-center gap-2">
          <Input
            type="date"
            value={dateFrom}
            onChange={(e) => setDateFrom(e.target.value)}
            className="w-[150px]"
            data-testid="date-from"
          />
          <span className="text-muted-foreground">to</span>
          <Input
            type="date"
            value={dateTo}
            onChange={(e) => setDateTo(e.target.value)}
            className="w-[150px]"
            data-testid="date-to"
          />
        </div>
        {(dateFrom || dateTo) && (
          <Button 
            variant="ghost" 
            size="sm" 
            onClick={() => { setDateFrom(''); setDateTo(''); }}
          >
            Clear
          </Button>
        )}
      </div>

      {/* Filters */}
      <div className="flex flex-col md:flex-row gap-4 mb-6">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input
            placeholder="Search by patient name or activity..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10"
            data-testid="activity-search"
          />
        </div>
        <Select value={sortBy} onValueChange={setSortBy}>
          <SelectTrigger className="w-[180px]">
            <ArrowUpDown className="w-4 h-4 mr-2" />
            <SelectValue placeholder="Sort by" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="created_at">Time</SelectItem>
            <SelectItem value="user_name">Patient Name</SelectItem>
            <SelectItem value="activity_type">Activity Type</SelectItem>
          </SelectContent>
        </Select>
        <Button
          variant="outline"
          size="icon"
          onClick={() => setSortOrder(sortOrder === "desc" ? "asc" : "desc")}
        >
          {sortOrder === "desc" ? <ArrowDown className="w-4 h-4" /> : <ArrowUp className="w-4 h-4" />}
        </Button>
      </div>

      {/* Activity List */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-lg flex items-center justify-between">
            <span>Recent Activities</span>
            <Badge variant="secondary">{filteredActivities.length} activities</Badge>
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          <div className="divide-y max-h-[600px] overflow-y-auto">
            {filteredActivities.length > 0 ? (
              filteredActivities.map((activity, index) => (
                <div 
                  key={activity.id || index}
                  className={`p-4 hover:bg-muted/50 transition-colors ${
                    activity.severity === 'alert' ? 'bg-red-50/50' : 
                    activity.severity === 'warning' ? 'bg-amber-50/50' : ''
                  }`}
                >
                  <div className="flex items-start gap-4">
                    <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                      activity.severity === 'alert' ? 'bg-red-100' : 
                      activity.severity === 'warning' ? 'bg-amber-100' : 'bg-primary/10'
                    }`}>
                      {getActivityIcon(activity.activity_type)}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <button
                          onClick={() => loadPatientDetails(activity.user_id, activity.user_name)}
                          className="font-medium text-primary hover:underline"
                          data-testid={`patient-link-${activity.user_id}`}
                        >
                          {activity.user_name || "Unknown Patient"}
                        </button>
                        <Badge variant="outline" className="text-xs">
                          {activity.activity_type}
                        </Badge>
                        {activity.severity === 'alert' && (
                          <Badge className="bg-red-500 text-white text-xs">
                            <AlertTriangle className="w-3 h-3 mr-1" />
                            Alert
                          </Badge>
                        )}
                      </div>
                      <p className="text-sm font-medium mt-1">{activity.title}</p>
                      <p className="text-sm text-muted-foreground line-clamp-2">{activity.description}</p>
                      {activity.data?.symptoms?.length > 0 && (
                        <div className="flex flex-wrap gap-1 mt-2">
                          {activity.data.symptoms.map((symptom, i) => (
                            <Badge key={i} variant="secondary" className="text-xs">{symptom}</Badge>
                          ))}
                        </div>
                      )}
                    </div>
                    <div className="text-right flex-shrink-0">
                      <span className="text-xs text-muted-foreground">
                        <Clock className="w-3 h-3 inline mr-1" />
                        {formatTime(activity.created_at)}
                      </span>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="mt-2"
                        onClick={() => loadPatientDetails(activity.user_id, activity.user_name)}
                      >
                        <Eye className="w-4 h-4 mr-1" />
                        Details
                      </Button>
                    </div>
                  </div>
                </div>
              ))
            ) : (
              <div className="p-12 text-center text-muted-foreground">
                <Activity className="w-12 h-12 mx-auto mb-3 opacity-50" />
                <p>No activities found</p>
                <p className="text-sm mt-1">Try adjusting your filters</p>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Patient Details Modal */}
      <Dialog open={!!selectedPatient} onOpenChange={() => { setSelectedPatient(null); setPatientDetails(null); }}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Users className="w-5 h-5 text-primary" />
              {selectedPatient?.name || "Patient"} - Activity Details
            </DialogTitle>
          </DialogHeader>
          
          {detailsLoading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-8 h-8 animate-spin text-primary" />
            </div>
          ) : patientDetails ? (
            <div className="space-y-6 py-4">
              {/* Summary Stats */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <Card>
                  <CardContent className="pt-4 pb-4 text-center">
                    <Heart className="w-5 h-5 mx-auto text-pink-500 mb-1" />
                    <p className="text-2xl font-bold">{patientDetails.summary?.total_checkins || 0}</p>
                    <p className="text-xs text-muted-foreground">Check-ins</p>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="pt-4 pb-4 text-center">
                    <FileText className="w-5 h-5 mx-auto text-blue-500 mb-1" />
                    <p className="text-2xl font-bold">{patientDetails.summary?.total_journals || 0}</p>
                    <p className="text-xs text-muted-foreground">Journal Entries</p>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="pt-4 pb-4 text-center">
                    <Pill className="w-5 h-5 mx-auto text-green-500 mb-1" />
                    <p className="text-2xl font-bold">{patientDetails.summary?.total_med_logs || 0}</p>
                    <p className="text-xs text-muted-foreground">Med Logs</p>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="pt-4 pb-4 text-center">
                    <TrendingUp className="w-5 h-5 mx-auto text-amber-500 mb-1" />
                    <p className="text-2xl font-bold">{patientDetails.summary?.avg_pain_level || 0}</p>
                    <p className="text-xs text-muted-foreground">Avg Pain</p>
                  </CardContent>
                </Card>
              </div>

              {/* Mood Distribution */}
              {patientDetails.summary?.mood_distribution && Object.keys(patientDetails.summary.mood_distribution).length > 0 && (
                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm">Mood Distribution (Last 30 Days)</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="flex flex-wrap gap-2">
                      {Object.entries(patientDetails.summary.mood_distribution).map(([mood, count]) => (
                        <Badge key={mood} variant="outline" className="capitalize">
                          {mood}: {count}
                        </Badge>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Recent Check-ins */}
              {patientDetails.checkins?.length > 0 && (
                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm">Recent Check-ins</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-2 max-h-48 overflow-y-auto">
                      {patientDetails.checkins.slice(0, 5).map((checkin, i) => (
                        <div key={i} className="flex items-center justify-between p-2 bg-muted/50 rounded">
                          <div>
                            <span className="text-sm capitalize">{checkin.mood || "N/A"}</span>
                            <span className="text-xs text-muted-foreground ml-2">
                              Pain: {checkin.pain_level}/10
                            </span>
                          </div>
                          <span className="text-xs text-muted-foreground">
                            {new Date(checkin.timestamp).toLocaleDateString()}
                          </span>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Assigned Staff */}
              {patientDetails.assignments?.length > 0 && (
                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm">Assigned Staff</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="flex flex-wrap gap-2">
                      {patientDetails.assignments.map((assignment, i) => (
                        <Badge key={i} variant="secondary">
                          {assignment.staff_name} ({assignment.assignment_type})
                        </Badge>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}
            </div>
          ) : (
            <div className="text-center py-8 text-muted-foreground">
              No details available
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Activity List Dialog */}
      <Dialog open={activityDialogOpen} onOpenChange={setActivityDialogOpen}>
        <DialogContent className="max-w-4xl max-h-[80vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle>
              {ACTIVITY_TYPES.find(t => t.value === activityDialogType)?.label || 'Activities'} 
              ({getActivitiesByType(activityDialogType).length})
            </DialogTitle>
          </DialogHeader>
          <div className="flex-1 overflow-y-auto">
            {getActivitiesByType(activityDialogType).length === 0 ? (
              <div className="text-center py-12 text-muted-foreground">
                <Activity className="w-12 h-12 mx-auto mb-4 opacity-50" />
                <p>No activities found</p>
              </div>
            ) : (
              <div className="space-y-3">
                {getActivitiesByType(activityDialogType).map((activity, idx) => {
                  const TypeIcon = ACTIVITY_TYPES.find(t => t.value === activity.activity_type)?.icon || Activity;
                  return (
                    <div 
                      key={idx}
                      className="p-4 border rounded-lg hover:bg-slate-50 transition-colors"
                    >
                      <div className="flex items-start gap-3">
                        <div className="p-2 bg-primary/10 rounded-lg">
                          <TypeIcon className="w-4 h-4 text-primary" />
                        </div>
                        <div className="flex-1">
                          <div className="flex items-center justify-between">
                            <p className="font-medium">{activity.user_name}</p>
                            <span className="text-xs text-muted-foreground">
                              {new Date(activity.created_at).toLocaleString()}
                            </span>
                          </div>
                          <p className="text-sm text-muted-foreground mt-1">{activity.title}</p>
                          {activity.description && (
                            <p className="text-sm mt-2 line-clamp-2">{activity.description}</p>
                          )}
                          {activity.activity_type === 'medication' && activity.medications && (
                            <div className="mt-2 flex flex-wrap gap-2">
                              {activity.medications.map((med, i) => (
                                <Badge key={i} variant="outline" className="text-xs">
                                  <Pill className="w-3 h-3 mr-1" />
                                  {med.name} - {med.dosage}
                                </Badge>
                              ))}
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setActivityDialogOpen(false)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
